import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function DeviceList() {
  const [devices, setDevices] = useState([]);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [firmwareFiles, setFirmwareFiles] = useState([]);
  const [version, setVersion] = useState('');
  const [selectedUrl, setSelectedUrl] = useState('');
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const fetchDevices = async () => {
    try {
      const res = await API.get('/devices');
      setDevices(Array.isArray(res.data) ? res.data : res.data.devices || []);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch devices');
    }
  };

  useEffect(() => {
    fetchDevices();
  }, []);

  const handleDelete = async (deviceId) => {
    if (!window.confirm('Are you sure you want to delete this device?')) return;
    try {
      await API.delete(`/devices/${deviceId}`);
      fetchDevices();
    } catch {
      alert('Failed to delete device');
    }
  };

  const handleUploadClick = () => fileInputRef.current.click();

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${BASE_URL}/superadmin/upload-firmware`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      alert(`✅ Firmware uploaded: ${data.filename || file.name}`);
    } catch (err) {
      console.error(err);
      alert('❌ Failed to upload firmware');
    }
  };

  const openBroadcastModal = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BASE_URL}/superadmin/firmware-files`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Failed to fetch files');
      const data = await res.json();
      setFirmwareFiles(data.files || []);
      setShowModal(true);
    } catch (err) {
      console.error(err);
      alert('Failed to load firmware files');
    }
  };

  const handleBroadcast = async () => {
    if (!version.trim() || !selectedUrl) {
      alert('Please enter a version and select a file.');
      return;
    }

    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const request_id =
      now.getFullYear() +
      pad(now.getMonth() + 1) +
      pad(now.getDate()) +
      pad(now.getHours()) +
      pad(now.getMinutes()) +
      pad(now.getSeconds()) +
      '-0001';

    const payload = {
      request_id,
      command: 'firmware_update',
      version,
      value: selectedUrl
    };

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BASE_URL}/superadmin/broadcast-firmware`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          topic: 'ipqs/universal',
          payload
        })
      });

      if (!res.ok) throw new Error('Broadcast failed');
      alert('✅ Firmware broadcast successful');
      setShowModal(false);
      setVersion('');
      setSelectedUrl('');
    } catch (err) {
      console.error(err);
      alert('❌ Failed to broadcast firmware');
    }
  };

  return (
    <div style={{ padding: 20 }}>
      <h2>Device List</h2>

      <div style={{ marginBottom: 10 }}>
        <button onClick={() => navigate('/devices/create')}>+ Create Device</button>
        &nbsp;
        <button onClick={handleUploadClick}>Upload OTA</button>
        &nbsp;
        <button onClick={openBroadcastModal}>Broadcast Firmware Update</button>

        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
          accept=".bin,.hex,.zip"
        />
      </div>

      {error && <p style={{ color: 'red' }}>{error}</p>}

      {Array.isArray(devices) ? (
        <table border="1" cellPadding="10">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Topic</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {devices.map((d) => (
              <tr key={d.device_id}>
                <td>{d.device_id}</td>
                <td>{d.device_name}</td>
                <td>{d.topic_name}</td>
                <td>{d.status}</td>
                <td>
                  <button onClick={() => navigate(`/devices/edit/${d.device_id}`)}>Edit</button>
                  <button onClick={() => handleDelete(d.device_id)}>Delete</button>
                  <button onClick={() => navigate(`/devices/configure/${d.device_id}`)}>Configure</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p>Loading devices...</p>
      )}

      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: '#f9f9f9',
            padding: 20,
            border: '1px solid #999',
            zIndex: 1000,
            width: 400
          }}
        >
          <h3>Broadcast Firmware</h3>

          <label>Version:</label>
          <input
            type="text"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            style={{ width: '100%' }}
          />

          <br /><br />

          <label>Select File:</label>
          <select
            value={selectedUrl}
            onChange={(e) => setSelectedUrl(e.target.value)}
            style={{ width: '100%' }}
          >
            <option value="">-- Select File --</option>
            {firmwareFiles.map((file) => (
              <option key={file.url} value={file.url}>
                {file.filename}
              </option>
            ))}
          </select>

          <br /><br />

          <button onClick={handleBroadcast}>Send Firmware</button>
          &nbsp;
          <button onClick={() => setShowModal(false)}>Cancel</button>
        </div>
      )}
    </div>
  );
}
